import React from "react";
import JSZip from "jszip";
import { v4 as uuidv4 } from "uuid";

function JSONGenerator({ files }) {
  const generateJSON = () => {
    const newTemplate = {
      "namespace": "chest",
      "chest_label": {},
      "ofunnys_chest_panel": {
        "type": "image",
        // Updated size to match 256x512
        "size": [256, 512],
        "layer": 11,
        "offset": [0, 40],
        "$localtitle": "$container_title",
      },
      "ofunnys_ia_categories@ofunnys_chest_panel": {
        "texture": "textures/ui/inventory/ia/ia_categories",
        "visible": "(not ($localtitle - '' = $localtitle))",
      },
      "ofunnys_ia_crafting@ofunnys_chest_panel": {
        "texture": "textures/ui/inventory/ia/ia_crafting",
        "visible": "(not ($localtitle - '' = $localtitle))",
      },
      "ofunnys_chest_panels": {
        "type": "panel",
        "controls": [
          { "ofunnys_ia_categories@ofunnys_ia_categories": {} },
          { "ofunnys_ia_crafting@ofunnys_ia_crafting": {} },
        ],
      },
      "large_chest_panel": {
        "modifications": [
          {
            "array_name": "controls",
            "operation": "insert_back",
            "value": {
              "ofunnys_chest_panels@ofunnys_chest_panels": {},
            },
          },
        ],
      },
      "small_chest_panel": {
        "modifications": [
          {
            "array_name": "controls",
            "operation": "insert_back",
            "value": {
              "ofunnys_chest_panels@ofunnys_chest_panels": {},
            },
          },
        ],
      },
      "small_chest_screen@common.inventory_screen_common": {
        "$atitle": "$container_title",
        "$ofunnymenu": "(('%.2s' * $atitle) = '§')",
        "variables": [
          {
            "requires": "($desktop_screen or $ofunnymenu)",
            "$screen_content": "chest.small_chest_panel",
            "$screen_bg_content": "common.screen_background",
            "$screen_background_alpha": 0.4,
          },
          {
            "requires": "($pocket_screen and not $ofunnymenu)",
            "$use_custom_pocket_toast": true,
            "$screen_content": "pocket_containers.small_chest_panel",
          },
        ],
      },
      "large_chest_screen@common.inventory_screen_common": {
        "$atitle": "$container_title",
        "$ofunnymenu": "(('%.2s' * $atitle) = '§')",
        "variables": [
          {
            "requires": "($desktop_screen or $ofunnymenu)",
            "$screen_content": "chest.large_chest_panel",
            "$screen_bg_content": "common.screen_background",
            "$screen_background_alpha": 0.4,
          },
          {
            "requires": "($pocket_screen and not $ofunnymenu)",
            "$use_custom_pocket_toast": true,
            "$screen_content": "pocket_containers.large_chest_panel",
          },
        ],
      },
    };

    files.forEach(({ file, char, originalFileName, hashedName }) => {
      const finalName = hashedName || (file && file.name ? file.name : originalFileName);
      const baseName = finalName.replace(/\.png$/i, "");
      
      const entry = {
        [`${baseName}@ofunnys_chest_panel`]: {
          "texture": `textures/ui/pogmc_converter/${finalName}`,
          "visible": `(not ($localtitle - '${char}' = $localtitle))`,
        },
      };

      newTemplate.ofunnys_chest_panels.controls.push({
        [`${baseName}@${baseName}`]: {},
      });

      Object.assign(newTemplate, entry);
    });

    return newTemplate;
  };

  const generateManifest = () => ({
    "format_version": 2,
    "header": {
      "description": "PogMCConverter UI Pack",
      "name": "PogMCConverter UI Pack",
      "uuid": uuidv4(),
      "version": [1, 0, 5],
      "min_engine_version": [1, 20, 0],
    },
    "modules": [
      {
        "description": "PogMCConverter UI Pack",
        "type": "resources",
        "uuid": uuidv4(),
        "version": [1, 0, 5],
      },
    ],
  });

  const generateUICommon = () => ({
    "namespace": "common",
    "common_panel/close_button_holder": {},
  });

  const downloadMCpack = async () => {
    const zip = new JSZip();
    const uiFolder = zip.folder("ui");
    const texturesFolder = zip.folder("textures/ui/pogmc_converter");

    // Add chest_screen.json (merged)
    const chestScreenJSON = generateJSON();
    uiFolder.file("chest_screen.json", JSON.stringify(chestScreenJSON, null, 2));

    // Add ui_common.json
    const uiCommonJSON = generateUICommon();
    uiFolder.file("ui_common.json", JSON.stringify(uiCommonJSON, null, 2));

    // Add manifest.json
    const manifestJSON = generateManifest();
    zip.file("manifest.json", JSON.stringify(manifestJSON, null, 2));

    // Save offset values in offsets.json.
    // Now storing xOffset, yOffset, and includeBackground.
    const offsetsData = {};
    files.forEach(({ file, originalFileName, hashedName, xOffset, yOffset, includeBackground }) => {
      const finalFileName =
        (hashedName ||
          (file && file.name ? file.name : originalFileName)).toLowerCase().endsWith(".png")
          ? (hashedName || (file && file.name ? file.name : originalFileName))
          : (hashedName || (file && file.name ? file.name : originalFileName)) + ".png";
      offsetsData[finalFileName] = { xOffset, yOffset, includeBackground };
    });
    uiFolder.file("offsets.json", JSON.stringify(offsetsData, null, 2));

    // Add all PNG files with offsets applied
    for (const { file, processedBlob, originalFileName, hashedName } of files) {
      if (processedBlob) {
        const fileData = await processedBlob.arrayBuffer();
        const rawName = hashedName || (file && file.name ? file.name : originalFileName);
        const finalName = rawName.toLowerCase().endsWith(".png") ? rawName : rawName + ".png";
        texturesFolder.file(finalName, fileData);
      } else {
        console.warn(`Processed blob missing for file: ${file?.name || originalFileName}`);
      }
    }
    

    // Generate zip and rename to .mcpack
    zip.generateAsync({ type: "blob" }).then((content) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(content);
      link.download = "pogmc_converter_uis.mcpack";
      link.click();
    });
  };

  return (
    <div className="json-generator">
      <button onClick={downloadMCpack} className="download-button">
        Download PogMCConverter Pack (.mcpack)
      </button>
    </div>
  );
}

export default JSONGenerator;
